export const paywallPriceStatuses = {
  active: "active",
  inactive: "inactive",
  draft: "draft",
} as const;

export type PaywallPriceStatus =
  (typeof paywallPriceStatuses)[keyof typeof paywallPriceStatuses];

export const paywallPriceTypes = {
  onetime: "onetime",
  subscription: "subscription",
  installments: "installments",
} as const;
export type PaywallPriceType = keyof typeof paywallPriceTypes;

export const paywallPriceIntervals = {
  daily: "daily",
  weekly: "weekly",
  monthly: "monthly",
  yearly: "yearly",
} as const;
export type PaywallPriceInterval = keyof typeof paywallPriceIntervals;

export const paywallPriceFrequencies = {
  daily: "daily",
  weekly: "weekly",
  biweekly: "biweekly",
  monthly: "monthly",
  bimonthly: "bimonthly",
  quarterly: "quarterly",
  biannually: "biannually",
  yearly: "yearly",
  onetime: "onetime",
} as const;
export type PaywallPriceFrequency = keyof typeof paywallPriceFrequencies;

export interface PaywallPriceBuildOptionsFrequency {
  as_adjective: string;
  as_adverb: string;
  as_phrase: string;
  interval: PaywallPriceInterval;
  interval_count: number;
}

export interface PaywallPriceBuildOptionsEntry {
  frequencies: {
    [frequency: string]: PaywallPriceBuildOptionsFrequency;
  };
  limits: {
    [limit: string]: {
      min: number;
      max: number;
    };
  };
  name: string;
  trial_options: any;
}

export type PaywallPriceScenarioCamelCase =
  | "value" // onetime | subscrition | installments
  | "trialThenValue" // trial + subscription | trial + installments
  | "upfrontThenValue" // upfront + subscription | upfront + installments
  | "trialThenUpfrontThenValue" // trial + upfront + subscription | trial + upfront + installments
  | "upfrontThenTrialThenValue"; // upfront + trial + subscription | upfront + trial + installments
export type PaywallPriceScenario =
  | "value" // onetime | subscrition | installments
  | "trial_then_value" // trial + subscription | trial + installments
  | "upfront_then_value" // upfront + subscription | upfront + installments
  | "trial_then_upfront_then_value" // trial + upfront + subscription | trial + upfront + installments
  | "upfront_then_trial_then_value"; // upfront + trial + subscription | upfront + trial + installments

export interface PaywallPriceBuildOptions {
  [type: string]: PaywallPriceBuildOptionsEntry;
}

export interface BasePaywallPrice {
  amount: number;
  currency_id: number;
  display_amount: string;
  display_name?: string;
  id: number;
  installments_count?: number;
  interval?: PaywallPriceInterval;
  interval_count?: number;
  interval_qualifier: string;
  // These props come from the presenter/serializer layer
  interval_type: PaywallPriceFrequency;
  is_web_price?: boolean;

  payable_payment_methods: Array<string>; // TODO: what's this for? Could we remove it once PMC is the default?
  preferred: boolean;
  price_type: PaywallPriceType;
  scenario: PaywallPriceScenario;
  short_description?: string;
  status?: PaywallPriceStatus;
  subtitle?: string;
  title: string;
  trial_days?: number;
  trial_enabled: boolean;
  trial_requires_upfront_payment: boolean;
  upfront_payment_amount?: number;
  value: string;
}
interface BaseRecurringPaywallPrice extends BasePaywallPrice {
  interval: PaywallPriceInterval;
  interval_count: number;
}
export interface OnetimePaywallPrice extends BasePaywallPrice {
  price_type: "onetime";
}
export interface SubscriptionPaywallPrice extends BaseRecurringPaywallPrice {
  price_type: "subscription";
}
export interface InstallmentsPaywallPrice extends BaseRecurringPaywallPrice {
  installments_count: number;
  price_type: "installments";
}
export type PaywallPrice =
  | OnetimePaywallPrice
  | SubscriptionPaywallPrice
  | InstallmentsPaywallPrice;
